// useAnchoredCollapse — Iter160t (Randy pt-ytre-chevron-no-jump, Feb 2026).
//
// Platform primitive for card-collapse patterns that need to feel FLUID
// (smooth height animation) and stable (no page-jump when the card resizes).
//
// Two flavors:
//   • useAnchoredCollapse({ initialCollapsed })
//       Binary open/closed toggle. Returns { collapsed, toggle, cardRef }.
//   • useThreeStageChevron({ initialStage })
//       Three-stage palindromic (full ↔ mid ↔ header-only). Returns
//       { stage, cycle, cardRef, isFull, isMid, isHeaderOnly, iconHint }.
//
// Both flavors expose a `cardRef` you MUST attach to the outer card
// div. On every state change the hook:
//   1. Reads the card's `boundingClientRect().top` before React commits.
//   2. Schedules a double-rAF measurement AFTER the commit + first
//      transition frame.
//   3. If the card's top shifted (content above resized), compensates
//      via window.scrollBy(delta, 'instant').
// Result: the CLICKED card stays visually pinned to the same viewport
// position; content above/below reflows around it, but the user's focus
// doesn't jump.
//
// Pair with the CSS "grid-template-rows 0fr↔1fr" collapse pattern for
// smooth height animation. Recommended wrapper:
//
//   <div
//     className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
//     style={{ gridTemplateRows: visible ? "1fr" : "0fr" }}
//   >
//     <div className="min-h-0">
//       {content}
//     </div>
//   </div>
//
// The min-h-0 inner div is required — otherwise a grid item's default
// min-height:auto keeps the row at its content height and the animation
// never actually collapses.

import { useState, useCallback, useRef } from "react";

// Internal helper — the anchor-scroll compensation. Called AFTER the
// state change; measures the card's top delta and scrollBy to keep it
// visually pinned.
function useAnchorCompensation(cardRef) {
  return useCallback((before) => {
    if (before == null) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const after = cardRef.current?.getBoundingClientRect().top ?? null;
        if (after != null && Math.abs(after - before) > 0.5) {
          window.scrollBy({ top: after - before, behavior: "instant" });
        }
      });
    });
  }, [cardRef]);
}

/**
 * Anchor-scroll helper for cases where the collapse state is managed
 * EXTERNALLY (server-persisted, parent-controlled, etc.).
 *
 * Usage:
 *   const { cardRef, withAnchor } = useAnchoredScrollTarget();
 *   const toggle = () => withAnchor(() => setCollapsed(v => !v));
 *   return <div ref={cardRef}>...</div>;
 *
 * `withAnchor(fn)` measures the card top BEFORE `fn`, runs `fn`
 * (which triggers the state change), then compensates scroll delta
 * after React commits + transition first-frame.
 */
export function useAnchoredScrollTarget() {
  const cardRef = useRef(null);
  const compensate = useAnchorCompensation(cardRef);
  const withAnchor = useCallback((fn) => {
    const before = cardRef.current?.getBoundingClientRect().top ?? null;
    fn();
    compensate(before);
  }, [compensate]);
  return { cardRef, withAnchor };
}

/**
 * Binary open/closed toggle with anchor-scroll compensation.
 * @param {Object} opts
 * @param {boolean} opts.initialCollapsed default false (open)
 */
export function useAnchoredCollapse({ initialCollapsed = false } = {}) {
  const [collapsed, setCollapsed] = useState(!!initialCollapsed);
  const cardRef = useRef(null);
  const compensate = useAnchorCompensation(cardRef);
  const toggle = useCallback(() => {
    const before = cardRef.current?.getBoundingClientRect().top ?? null;
    setCollapsed((prev) => !prev);
    compensate(before);
  }, [compensate]);
  const setState = useCallback((val) => {
    const before = cardRef.current?.getBoundingClientRect().top ?? null;
    setCollapsed(val);
    compensate(before);
  }, [compensate]);
  return { collapsed, toggle, setCollapsed: setState, cardRef };
}

/**
 * Three-stage palindromic chevron.
 *   Stage 0 = fully expanded (full body + footer)
 *   Stage 1 = mid           (short preview + footer) ← default
 *   Stage 2 = header-only   (nothing but the header)
 *
 * Cycle: triangle-wave. Default is stage 1 with initial direction -1
 * (first click expands to stage 0). Bounces at both ends:
 *   1 → 0 → 1 → 2 → 1 → 0 → 1 → 2 → …
 *
 * State (stage, dir) is stored in ONE atom so the setState updater is
 * pure — required for correctness under React StrictMode (which double-
 * invokes updater functions).
 */
export function useThreeStageChevron({ initialStage = 1 } = {}) {
  const [{ stage, dir }, setChev] = useState({ stage: initialStage, dir: -1 });
  const cardRef = useRef(null);
  const compensate = useAnchorCompensation(cardRef);
  const cycle = useCallback(() => {
    const before = cardRef.current?.getBoundingClientRect().top ?? null;
    setChev((prev) => {
      let next = prev.stage + prev.dir;
      let newDir = prev.dir;
      if (next < 0) { newDir = 1; next = prev.stage + 1; }
      else if (next > 2) { newDir = -1; next = prev.stage - 1; }
      return { stage: next, dir: newDir };
    });
    compensate(before);
  }, [compensate]);
  // Iter172 (Randy pt-ytre-minimize-old, Feb 13, 2026) — imperative
  // setter so parents can drive a "minimize all except X" batch
  // action without touching each card's internal state. Adjusts
  // `dir` so the next chevron click flows naturally (from stage 2
  // the next click expands back to 1; from stage 0 the next click
  // collapses to 1).
  const setStage = useCallback((newStage) => {
    const before = cardRef.current?.getBoundingClientRect().top ?? null;
    setChev((prev) => {
      const clamped = Math.max(0, Math.min(2, newStage));
      const newDir = clamped === 0 ? 1 : clamped === 2 ? -1 : prev.dir;
      return { stage: clamped, dir: newDir };
    });
    compensate(before);
  }, [compensate]);
  return {
    stage,
    cycle,
    setStage,
    cardRef,
    isFull: stage === 0,
    isMid: stage === 1,
    isHeaderOnly: stage === 2,
    iconHint: stage === 0 ? "collapse" : stage === 2 ? "expand" : "mid",
  };
}
